import { useEffect } from 'react';

import { Accordion, Card, H1, H2, ListRow, Logo, ShimmerText, Tag, Text, VStack } from '@betterment/samba-react';

import { ErrorBoundary } from '~/b4b/components/error-boundary/ErrorBoundary';
import { SambaAutoLink } from '~/b4b/components/SambaAutoLink';
import { SambaIconAutoLink } from '~/b4b/components/SambaIconAutoLink';
import Footer from '~/b4b/features/layout/footer/Footer';
import { WorkflowLayout } from '~/b4b/features/layout/WorkflowLayout';
import { navigate } from '~/b4b/utils/pathHelpers';
import { urlBuilder } from '~/b4b/utils/urlBuilder';

import { statusLabel } from './statusLabel';
import { Employer, LoadingStatus, useEmployerSelectData } from './useEmployerSelectData';

const LoadingEmployerRow = () => (
  <Card css={{ boxShadow: 'none', borderStyle: 'solid', borderWidth: 'thin', borderColor: 'border.weak' }}>
    <Card.Content>
      <ListRow css={{ p: 'space.200' }}>
        <ListRow.LeadingContent>
          <ShimmerText textStyle="text100" css={{ height: '16px', width: '60%' }} />
        </ListRow.LeadingContent>
        <ListRow.TrailingAsset>
          <ShimmerText textStyle="text50" css={{ height: '16px', width: 'size.200' }} />
        </ListRow.TrailingAsset>
      </ListRow>
    </Card.Content>
  </Card>
);

const TaskBadge = ({ tasksLoadingStatus, count }: { tasksLoadingStatus: LoadingStatus; count?: number }) => {
  if (tasksLoadingStatus === 'idle' || tasksLoadingStatus === 'loading') {
    return <ShimmerText textStyle="text50" css={{ height: '16px', width: 'size.200' }} />;
  }

  if (count !== undefined && count > 0) {
    return (
      <Tag accent="blue">
        <Tag.Text>{count}</Tag.Text>
      </Tag>
    );
  }

  return null;
};

const EmployerRow = ({
  employer,
  tasksLoadingStatus,
  taskCount,
  label,
}: {
  employer: Employer;
  tasksLoadingStatus: LoadingStatus;
  taskCount?: number;
  label?: string | null;
}) => (
  <SambaAutoLink
    css={{
      w: '100%',
      color: 'inherit',
      _hover: {
        color: 'inherit',
        textDecoration: 'none',
      },
      _active: {
        color: 'inherit',
        textDecoration: 'none',
      },
    }}
    to={urlBuilder.currentEmployer({ employerId: employer.id })}
  >
    <Card css={{ boxShadow: 'none', borderStyle: 'solid', borderWidth: 'thin', borderColor: 'border.weak' }}>
      <Card.Content>
        <ListRow css={{ p: 'space.200' }}>
          <ListRow.LeadingContent>
            <Text variant="text100">{employer.name}</Text>
            {label && (
              <Text variant="text50" color="text.weak">
                {label}
              </Text>
            )}
          </ListRow.LeadingContent>
          <ListRow.TrailingAsset>
            <TaskBadge tasksLoadingStatus={tasksLoadingStatus} count={taskCount} />
          </ListRow.TrailingAsset>
        </ListRow>
      </Card.Content>
    </Card>
  </SambaAutoLink>
);

const EmployerSelectPageContent = () => {
  const { activeEmployers, inactiveEmployers, taskCounts, loadingStatus, tasksLoadingStatus } = useEmployerSelectData();

  // Single employer → redirect to dashboard
  useEffect(() => {
    if (loadingStatus === 'loaded' && activeEmployers.length + inactiveEmployers.length === 1) {
      navigate(urlBuilder.dashboard());
    }
  }, [loadingStatus, activeEmployers.length, inactiveEmployers.length]);

  return (
    <>
      {loadingStatus === 'loading' && (
        <VStack css={{ w: '100%', gap: 'space.200' }}>
          <LoadingEmployerRow />
          <LoadingEmployerRow />
          <LoadingEmployerRow />
        </VStack>
      )}

      {loadingStatus === 'loaded' && (
        <VStack css={{ gap: 'space.100', w: '100%' }}>
          {activeEmployers.length > 0 && (
            <VStack css={{ gap: 'space.200' }}>
              <H2 variant="title200">Active ({activeEmployers.length})</H2>
              {activeEmployers.map(employer => (
                <EmployerRow
                  key={employer.id}
                  employer={employer}
                  tasksLoadingStatus={tasksLoadingStatus}
                  taskCount={taskCounts[employer.id]}
                />
              ))}
            </VStack>
          )}

          {inactiveEmployers.length > 0 && (
            <Accordion type="single" collapsible={true}>
              <Accordion.Item value="inactive" css={{ borderBottom: 'none' }}>
                <Accordion.Trigger>
                  <H2 variant="title200">Inactive ({inactiveEmployers.length})</H2>
                </Accordion.Trigger>
                <Accordion.Content>
                  <VStack css={{ gap: 'space.200', alignItems: 'stretch' }}>
                    {inactiveEmployers.map(employer => (
                      <EmployerRow
                        key={employer.id}
                        employer={employer}
                        tasksLoadingStatus={tasksLoadingStatus}
                        label={statusLabel(employer.plan?.status)}
                      />
                    ))}
                  </VStack>
                </Accordion.Content>
              </Accordion.Item>
            </Accordion>
          )}
        </VStack>
      )}
    </>
  );
};

export const EmployerSelectPage = () => {
  return (
    <WorkflowLayout pageTitle="Select plan">
      <WorkflowLayout.Header>
        <WorkflowLayout.Leading>
          <SambaAutoLink to={urlBuilder.dashboard()} aria-label="Back to dashboard">
            <Logo css={{ w: '226px' }} type="b4bSignature" />
          </SambaAutoLink>
        </WorkflowLayout.Leading>
        <WorkflowLayout.Content />
        <WorkflowLayout.Trailing>
          <SambaIconAutoLink name="X" label="Close" to={urlBuilder.dashboard()} />
        </WorkflowLayout.Trailing>
      </WorkflowLayout.Header>

      <WorkflowLayout.Main>
        <H1 css={{ mb: 'space.400' }}>Select plan</H1>
        <ErrorBoundary name="employerSelect" fallbackType="inline">
          <EmployerSelectPageContent />
        </ErrorBoundary>
      </WorkflowLayout.Main>

      <WorkflowLayout.Footer centered={false} sticky={false} withSeparator={false}>
        <Footer />
      </WorkflowLayout.Footer>
    </WorkflowLayout>
  );
};
